import type {
  MutateOptions,
  MutationFunctionContext,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { captureInteractiveOwner } from '@/lib/api/interactive-owner'
import {
  readRequestScopeGeneration,
  subscribeRequestScope,
} from '@/lib/api/request-scope'
import { useMutation } from '@tanstack/react-query'

import { SessionChangedError } from '@scrambl/shared/session-scope'

export type OwnedMutationFunctionContext = MutationFunctionContext & {
  /** Recheck after an awaited cancellation or reconciliation before publishing local effects. */
  assertOwnerCurrent(): Promise<void>
  /** Check synchronously at the publication/dispatch boundary after the awaited vault check. */
  assertOwnerCurrentSync(): void
}

type OwnedOptions<TData, TVariables, TContext> = Omit<
  UseMutationOptions<TData, Error, TVariables, TContext>,
  'mutationFn' | 'onMutate' | 'onSuccess' | 'onError' | 'onSettled'
> & {
  mutationFn(
    variables: TVariables,
    context: OwnedMutationFunctionContext,
  ): Promise<TData>
  onMutate?(
    variables: TVariables,
    context: OwnedMutationFunctionContext,
  ): TContext | Promise<TContext>
  onSuccess?(
    data: TData,
    variables: TVariables,
    result: TContext,
    context: OwnedMutationFunctionContext,
  ): unknown
  onError?(
    error: Error,
    variables: TVariables,
    result: TContext | undefined,
    context: OwnedMutationFunctionContext,
  ): unknown
  onSettled?(
    data: TData | undefined,
    error: Error | null,
    variables: TVariables,
    result: TContext | undefined,
    context: OwnedMutationFunctionContext,
  ): unknown
}
type OwnedVariables<T> = {
  value: T
  owner: ReturnType<typeof captureInteractiveOwner>
}

/** Interactive writes only. Durable offline commands retain their separate owner/version envelope. */
export function useOwnedMutation<
  TData = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: OwnedOptions<TData, TVariables, TContext>,
): UseMutationResult<TData, Error, TVariables, TContext> {
  // Mask results from a previous account/workspace even when this editor stays mounted.
  useSyncExternalStore(
    subscribeRequestScope,
    readRequestScopeGeneration,
    readRequestScopeGeneration,
  )
  // A retained callback belongs to its rendered view, not whichever account is current later.
  const renderedOwner = captureInteractiveOwner()
  const ownedContext = (
    input: OwnedVariables<TVariables>,
    context: MutationFunctionContext,
  ): OwnedMutationFunctionContext => ({
    ...context,
    assertOwnerCurrent: input.owner.assertCurrent,
    assertOwnerCurrentSync: input.owner.assertCurrentSync,
  })
  const canPublish = async (
    input: OwnedVariables<TVariables>,
    error?: Error | null,
  ) => {
    if (error instanceof SessionChangedError) return false
    try {
      await input.owner.assertCurrent()
      input.owner.assertCurrentSync()
      return true
    } catch {
      return false
    }
  }
  const mutation = useMutation<
    TData,
    Error,
    OwnedVariables<TVariables>,
    TContext
  >({
    ...options,
    // These APIs do not provide a durable operation receipt: never inherit retries.
    retry: false,
    meta: { ...options.meta, ...(options.onError ? { silent: true } : {}) },
    mutationFn: async (input, context) => {
      await input.owner.assertCurrent()
      input.owner.assertCurrentSync()
      try {
        const data = await options.mutationFn(
          input.value,
          ownedContext(input, context),
        )
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        return data
      } catch (error) {
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        throw error
      }
    },
    onMutate: async (input, context) => {
      await input.owner.assertCurrent()
      input.owner.assertCurrentSync()
      try {
        const result = await options.onMutate?.(
          input.value,
          ownedContext(input, context),
        )
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        return result as TContext
      } catch (error) {
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        throw error
      }
    },
    onSuccess: async (data, input, result, context) => {
      await input.owner.assertCurrent()
      input.owner.assertCurrentSync()
      try {
        await options.onSuccess?.(
          data,
          input.value,
          result,
          ownedContext(input, context),
        )
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
      } catch (error) {
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        throw error
      }
    },
    onError: async (error, input, result, context) => {
      if (!(await canPublish(input, error))) return
      try {
        input.owner.assertCurrentSync()
        await options.onError?.(
          error,
          input.value,
          result,
          ownedContext(input, context),
        )
      } catch (callbackError) {
        // Query Core reports errors from failure/settled handlers as unhandled rejections.
        // Ownership cancellation is expected; real current-owner callback defects still surface.
        if (
          callbackError instanceof SessionChangedError ||
          !(await canPublish(input))
        )
          return
        throw callbackError
      }
    },
    onSettled: async (data, error, input, result, context) => {
      if (!(await canPublish(input, error))) return
      try {
        input.owner.assertCurrentSync()
        await options.onSettled?.(
          data,
          error,
          input.value,
          result,
          ownedContext(input, context),
        )
      } catch (callbackError) {
        // Query Core reports errors from failure/settled handlers as unhandled rejections.
        // Ownership cancellation is expected; real current-owner callback defects still surface.
        if (
          callbackError instanceof SessionChangedError ||
          !(await canPublish(input))
        )
          return
        throw callbackError
      }
    },
  })
  const bindCallbacks = (
    callbacks?: MutateOptions<TData, Error, TVariables, TContext>,
  ): MutateOptions<TData, Error, OwnedVariables<TVariables>, TContext> => ({
    onSuccess: (data, input, result, context) => {
      if (input.owner.isCurrent())
        callbacks?.onSuccess?.(data, input.value, result, context)
    },
    onError: (error, input, result, context) => {
      if (input.owner.isCurrent() && !(error instanceof SessionChangedError))
        callbacks?.onError?.(error, input.value, result, context)
    },
    onSettled: (data, error, input, result, context) => {
      if (input.owner.isCurrent() && !(error instanceof SessionChangedError))
        callbacks?.onSettled?.(data, error, input.value, result, context)
    },
  })
  const current = !mutation.variables || mutation.variables.owner.isCurrent()
  const visible = current
    ? mutation
    : {
        ...mutation,
        status: 'idle' as const,
        isIdle: true,
        isPending: false,
        isSuccess: false,
        isError: false,
        data: undefined,
        error: null,
        context: undefined,
        variables: undefined,
        failureCount: 0,
        failureReason: null,
        submittedAt: 0,
        isPaused: false,
      }
  // The public facade retains original variables/context and the observer's discriminated states.
  return {
    ...visible,
    variables: current ? mutation.variables?.value : undefined,
    data: current ? mutation.data : undefined,
    context: current ? mutation.context : undefined,
    mutate: (value, callbacks) =>
      mutation.mutate(
        { value, owner: renderedOwner },
        bindCallbacks(callbacks),
      ),
    mutateAsync: async (value, callbacks) => {
      const input = { value, owner: renderedOwner }
      try {
        const data = await mutation.mutateAsync(input, bindCallbacks(callbacks))
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        return data
      } catch (error) {
        await input.owner.assertCurrent()
        input.owner.assertCurrentSync()
        throw error
      }
    },
  } as UseMutationResult<TData, Error, TVariables, TContext>
}
